import { useMemo, useState } from 'react';
import {
  orderedCategories,
  type HebergementCategory,
  type CategoryInput,
} from '@/lib/hebergement';
import IconPicker from '@/components/feature/IconPicker';
import ImagePicker from '@/components/feature/ImagePicker';

interface Props {
  categories: HebergementCategory[];
  createCategory: (input: CategoryInput) => Promise<HebergementCategory | null>;
  updateCategory: (id: number, patch: Partial<CategoryInput>) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
  onClose: () => void;
}

/**
 * Gestion des types d'hébergement (catégories + sous-types), sur le même modèle
 * que les catégories de produits : création, édition, suppression, icône ou
 * image de couverture. Les types par défaut (globaux) ne sont pas modifiables.
 */
export default function HebergementCategoriesModal({
  categories, createCategory, updateCategory, deleteCategory, onClose,
}: Props) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number>(0);
  const [tab, setTab] = useState<'icon' | 'image'>('icon');
  const [icon, setIcon] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [editing, setEditing] = useState<HebergementCategory | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const ordered = useMemo(() => orderedCategories(categories), [categories]);
  const roots = categories.filter((c) => !c.parent_id || c.parent_id === 0);

  const resetForm = () => {
    setName('');
    setParentId(0);
    setIcon(null);
    setImage(null);
    setTab('icon');
    setEditing(null);
  };

  const startEdit = (cat: HebergementCategory) => {
    setEditing(cat);
    setName(cat.nom);
    setParentId(cat.parent_id || 0);
    setIcon(cat.icon);
    setImage(cat.image_couverture);
    setTab(cat.image_couverture ? 'image' : 'icon');
    setMsg(null);
  };

  const save = async () => {
    const clean = name.trim();
    if (!clean) return;
    setSaving(true);
    setMsg(null);
    const payload: CategoryInput = {
      nom: clean,
      parent_id: parentId || 0,
      icon: tab === 'icon' ? icon : null,
      image_couverture: tab === 'image' ? image : null,
    };
    try {
      if (editing) {
        await updateCategory(editing.id, payload);
        setMsg("Type mis à jour avec succès");
      } else {
        const created = await createCategory(payload);
        if (!created) throw new Error('Échec');
        setMsg("Type d'hébergement créé avec succès");
      }
      resetForm();
    } catch {
      setMsg("Erreur lors de l'enregistrement du type.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteCategory(id);
      setConfirmId(null);
      if (editing?.id === id) resetForm();
    } catch {
      setMsg('Erreur lors de la suppression.');
    }
  };

  const isSuccess = !!msg && msg.includes('succès');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl border border-background-200/70 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground-950">
            <i className="ri-price-tag-3-line mr-2 text-primary-500"></i>
            Types d&apos;hébergement
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Formulaire création / édition */}
          <div className="pb-5 border-b border-background-200/70">
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Modifier un type existant</label>
            <select
              value={editing ? String(editing.id) : ''}
              onChange={(e) => {
                const id = Number(e.target.value);
                if (!id) { resetForm(); return; }
                const target = categories.find((c) => c.id === id);
                if (target) startEdit(target);
              }}
              className="w-full mb-3 px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="">— Nouveau type —</option>
              {ordered.map(({ cat, isChild }) => (
                <option key={cat.id} value={String(cat.id)}>
                  {isChild ? '↳ ' : ''}{cat.nom}{cat.idcommerce == null ? ' (par défaut)' : ''}
                </option>
              ))}
            </select>

            {editing && (
              <span className="inline-flex items-center gap-1 mb-3 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-50 text-accent-700">
                <i className="ri-edit-line"></i> Modification
              </span>
            )}
            <select
              value={String(parentId)}
              onChange={(e) => setParentId(Number(e.target.value))}
              className="w-full mb-3 px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="0">Type principal (aucun parent)</option>
              {roots.filter((c) => c.id !== editing?.id).map((c) => (
                <option key={c.id} value={String(c.id)}>Sous-type de : {c.nom}</option>
              ))}
            </select>

            <div className="flex items-center gap-2 mb-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
                placeholder="Ex. Chalet, Bungalow, Villa vue mer…"
                className="flex-1 px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
              />
              <button
                onClick={save}
                disabled={saving || !name.trim()}
                className="px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : editing ? 'Enregistrer' : 'Ajouter'}
              </button>
              {editing && (
                <button
                  onClick={resetForm}
                  className="px-3 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                >
                  Annuler
                </button>
              )}
            </div>

            {/* Onglets icône / image */}
            <div className="flex items-center gap-1 mb-3 bg-background-100 rounded-full p-1 w-fit">
              <button
                type="button"
                onClick={() => setTab('icon')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === 'icon' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-shapes-line mr-1"></i>Icône
              </button>
              <button
                type="button"
                onClick={() => setTab('image')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === 'image' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-image-line mr-1"></i>Image
              </button>
            </div>

            {tab === 'icon' ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIconPickerOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-shapes-line"></i>
                  <span className="whitespace-nowrap">{icon ? 'Changer' : 'Choisir une icône'}</span>
                </button>
                {icon && (
                  <div className="relative w-10 h-10 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-lg text-primary-600`}></i>
                    <button
                      onClick={() => setIcon(null)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-400 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-500"
                    >
                      <i className="ri-close-line text-[8px]"></i>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setImagePickerOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-image-line"></i>
                  <span className="whitespace-nowrap">{image ? 'Changer' : 'Choisir une image'}</span>
                </button>
                {image && (
                  <div className="relative w-12 h-10 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                    <img src={image} alt="Aperçu" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setImage(null)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-400 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-500"
                    >
                      <i className="ri-close-line text-[8px]"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {msg && (
            <div className={`px-3 py-2 rounded-lg text-xs ${isSuccess ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
              {msg}
            </div>
          )}

          {/* Liste */}
          {ordered.length === 0 ? (
            <p className="text-sm text-foreground-400 text-center py-4">Aucun type pour l&apos;instant</p>
          ) : (
            <div className="space-y-1">
              {ordered.map(({ cat, isChild }) => {
                const custom = cat.idcommerce != null;
                return (
                  <div
                    key={cat.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border border-background-200/70 ${
                      isChild ? 'ml-5 bg-background-100/50' : 'bg-background-50'
                    }`}
                  >
                    <span className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {cat.image_couverture ? (
                        <img src={cat.image_couverture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <i className={`${cat.icon || 'ri-building-4-line'} text-primary-600`}></i>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate">{cat.nom}</p>
                      {isChild && <p className="text-[11px] text-foreground-400">Sous-type</p>}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => startEdit(cat)}
                        title="Modifier"
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      {custom && (
                        confirmId === cat.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => remove(cat.id)}
                              className="px-2 py-1 bg-red-500 text-white rounded text-xs font-medium cursor-pointer hover:bg-red-600"
                            >
                              Confirmer
                            </button>
                            <button
                              onClick={() => setConfirmId(null)}
                              className="px-2 py-1 bg-background-100 text-foreground-600 rounded text-xs cursor-pointer hover:bg-background-200/70"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmId(cat.id)}
                            title="Supprimer"
                            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        )
                      )}
                      {!custom && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          Par défaut
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 h-16 border-t border-background-200/70 flex-shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            Terminer
          </button>
        </div>

        <IconPicker
          isOpen={iconPickerOpen}
          onClose={() => setIconPickerOpen(false)}
          onSelect={(iconClass) => { setIcon(iconClass); setImage(null); }}
          selectedIcon={icon}
        />
        <ImagePicker
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={(url) => { setImage(url); setIcon(null); }}
          selectedUrl={image}
        />
      </div>
    </div>
  );
}